import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { STATUS_LIST, STATUS_META } from "@/components/StatusPill";
import { PRIORITY_LIST, PRIORITY_META } from "@/components/PriorityPill";
import { TID } from "@/lib/testIds";

/**
 * Modal for creating/editing an item on a board. Sharp edges, orange border,
 * matches the defense-tech aesthetic.
 */
export default function ItemModal({ open, item, groups, onClose, onSave, onDelete }) {
    const [form, setForm] = useState(item || {});

    useEffect(() => {
        setForm(item || {});
    }, [item, open]);

    if (!open) return null;

    const isNew = !item?.id;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSave = () => {
        if (!form.name || !form.name.trim()) return;
        if (!form.group_id && groups.length) form.group_id = groups[0].id;
        onSave({
            name: form.name.trim(),
            status: form.status || "planning",
            priority: form.priority || "medium",
            owner: form.owner || "",
            start_date: form.start_date || null,
            due_date: form.due_date || null,
            notes: form.notes || "",
            group_id: form.group_id,
        });
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md">
            <div className="relative w-[520px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="flex items-center justify-between px-5 py-3 border-b border-virtus-line">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        {isNew ? "NEW ITEM" : "EDIT ITEM"}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-virtus-dim hover:text-virtus-orange"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <Field label="Item name">
                        <input
                            value={form.name || ""}
                            onChange={(e) => set("name", e.target.value)}
                            placeholder="Task 1"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            data-testid={TID.itemNameInput}
                            autoFocus
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Status">
                            <select
                                value={form.status || "planning"}
                                onChange={(e) => set("status", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {STATUS_LIST.map((s) => (
                                    <option key={s} value={s}>
                                        {STATUS_META[s].label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Priority">
                            <select
                                value={form.priority || "medium"}
                                onChange={(e) => set("priority", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {PRIORITY_LIST.map((p) => (
                                    <option key={p} value={p}>
                                        {PRIORITY_META[p].label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <Field label="Owner">
                        <input
                            value={form.owner || ""}
                            onChange={(e) => set("owner", e.target.value)}
                            placeholder="Owner"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Start">
                            <input
                                type="date"
                                value={form.start_date || ""}
                                onChange={(e) => set("start_date", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            />
                        </Field>
                        <Field label="Due">
                            <input
                                type="date"
                                value={form.due_date || ""}
                                onChange={(e) => set("due_date", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            />
                        </Field>
                    </div>
                    {groups && groups.length > 0 && (
                        <Field label="Group">
                            <select
                                value={form.group_id || groups[0].id}
                                onChange={(e) => set("group_id", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {groups.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    )}
                    <Field label="Notes">
                        <textarea
                            value={form.notes || ""}
                            onChange={(e) => set("notes", e.target.value)}
                            rows={3}
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </Field>
                </div>
                <div className="flex items-center justify-between px-5 py-3 border-t border-virtus-line">
                    <div>
                        {!isNew && (
                            <button
                                onClick={onDelete}
                                className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-stuck hover:text-white px-3 py-2 border border-virtus-stuck hover:bg-virtus-stuck"
                                data-testid={TID.itemDeleteBtn}
                            >
                                DELETE
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={handleSave}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange"
                            data-testid={TID.itemSaveBtn}
                        >
                            {isNew ? "CREATE" : "SAVE"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-2">
                {label}
            </div>
            {children}
        </div>
    );
}
